import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  type Edge,
} from "@xyflow/react";
import type { WorkflowEdgeData } from "./types";

function parseEdgeLabel(condition: string): string | null {
  if (!condition?.trim()) return null;
  const m = condition.match(/\{\{\s*nodes\.[^.]+\.output\.(\w+)\s*\}\}/);
  if (m) return m[1];
  return condition.length > 22 ? condition.slice(0, 19) + "…" : condition;
}

export default function AutomationEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps<Edge<WorkflowEdgeData>>) {
  const isError = data?.kind === "OnError";
  const label = parseEdgeLabel(data?.condition ?? "");

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.2,
  });

  const strokeColor = isError
    ? "#f87171"
    : selected
      ? "#3b82f6"
      : "#93c5fd";

  return (
    <>
      {selected && (
        <BaseEdge
          id={`${id}-glow`}
          path={edgePath}
          style={{
            stroke: isError ? "#ef4444" : "#3b82f6",
            strokeWidth: 8,
            strokeOpacity: 0.12,
            strokeLinecap: "round",
          }}
        />
      )}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: strokeColor,
          strokeWidth: selected ? 2 : 1.5,
          strokeDasharray: "7 5",
          strokeLinecap: "round",
          transition: "stroke 0.2s, stroke-width 0.2s",
        }}
      />
      <EdgeLabelRenderer>
        {(label || isError) && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            className="nodrag nopan"
          >
            <span
              className={`inline-flex items-center text-[9px] font-bold px-2.5 py-[3px] rounded-full border transition-all ${
                isError
                  ? "bg-red-50 text-red-500 border-red-200"
                  : "bg-blue-50 text-blue-500 border-blue-200"
              } ${selected ? "ring-1 ring-blue-300" : ""}`}
            >
              {isError && !label ? "Error" : label}
            </span>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
